import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background p-4">
    <div className="text-center max-w-md mx-auto p-8 bg-card border rounded-2xl shadow-sm">
      <div className="text-5xl font-bold text-destructive mb-4">!</div>
      <h1 className="text-3xl font-bold text-foreground mb-2">Something went wrong</h1>
      <p className="text-muted-foreground mb-8">
        An unexpected error occurred. You can go back to the previous page or reload to try again.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => window.history.back()}
          className="px-5 py-2 rounded-md border border-input bg-background hover:bg-muted text-foreground transition-colors"
        >
          Go back
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-5 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
        >
          Reload page
        </button>
      </div>
    </div>
  </div>
)

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />
    }
    return this.props.children
  }
}

export default ErrorBoundary
